import { useCallback, useContext, useEffect, useRef } from 'react'
import { RoomContext, RoomContextType } from '../RoomContext'
import {
    Room,
    RoomEvent,
    RemoteParticipant,
    RemoteTrackPublication,
    Participant,
    VideoPresets,
    LocalTrackPublication,
    DisconnectReason,
    ConnectionState,
    Track,
    MediaDeviceFailure,
    DataPacket_Kind,
    LocalParticipant,
    LocalVideoTrack,
    VideoQuality,
    VideoPreset,
} from 'livekit-client'
import { logger } from './Logger'
import { arrayDiff, arrayInPlaceRemove, extractVideoTrack, sleep } from '../utils'
import { SfuNode, UserContext, UserContextType } from '../UserContext'
import useRoomContextRef from './useRoomContextRef'
import applyLivekitStereoPatch from './applyLivekitStereoPatch'
import { LiveKitMetadata, MusePeer, PeerManifest } from './RoomContextMusePeer'
import { isDefinedDeviceId } from '../Logic'
import useRoomActions from './useRoomActions'
import useUserContextRef from './useUserContextRef'
import { wsConnection } from './WSConnectionInstance'
import { env } from './EnvironmentVariables'
import * as Constants from '../services/Constants'
import { sfuNodeSelection } from './SfuNodeSelection'
import useSfuAddPeersToContextAction from './useSfuAddPeersToContextAction'
import _ from 'lodash'
import useSfuIncomingMediaStateSynchronizer from './useSfuIncomingMediaStateSynchronizer'
import LiveKitManifestSupport from './LiveKitManifestSupport'
import { speakerDetectionManager } from './SpeakerDetectionManager'
import { videoSubscriptionManager } from './VideoSubscriptionManager'

const log = logger.createChildLogger({ name: 'SFURoomController' })

export default function useSfuRoomService() {
    const { state } = useContext(UserContext) as UserContextType
    const { state: roomState, dispatch: roomDispatch } = useContext(RoomContext) as RoomContextType

    const { roomStateRef } = useRoomContextRef()
    const { userStateRef } = useUserContextRef()

    const { leaveRoom, checkRemotePeersAudioEngineCompatibilityAndShowDialog, startCloudRecording, broadcastMessage } =
        useRoomActions()

    const { sfuAddPeersToContext, setLastManifest } = useSfuAddPeersToContextAction()

    const { onPeerAdded, onPeerRemoved, onTrackSubscribed, onTrackUnsubscribed, onManifestUpdated } =
        useSfuIncomingMediaStateSynchronizer()

    const peerDataChannelEventHandler = useCallback((obj: { type: string; data: any }, sessionId: string) => {
        const { type, data } = obj
        switch (type) {
            case 'peer-manifest-broadcast':
                if (!roomStateRef.current.peers[sessionId]) {
                    return
                }

                const doesSupport = LiveKitManifestSupport.isSupported(roomStateRef.current.peers[sessionId]?.sessionId)
                if (doesSupport) {
                    return
                }

                let manifest = data as PeerManifest

                onManifestUpdated(sessionId, manifest)

                setLastManifest(sessionId, manifest)

                roomDispatch({
                    type: 'update_peer_manifest',
                    value: {
                        sessionId: sessionId,
                        partialManifest: manifest,
                    },
                })
                break
        }
    }, [])

    // warning: must be cleared when entering new room, see resetRefsOnNewRoom().
    const prevSessionIdsRef = useRef<string[]>([])
    useEffect(
        function peerAddedEffect() {
            const room = roomStateRef.current.sfuRoom
            if (!room) {
                log.verbose('peerAddedEffect', 'sfuRoom is null; no-op.')
                return
            }

            // get sessionIds of peers who are connected.
            const sessionIds = Object.entries(roomState.peers)
                .filter(([_, peer]) => peer.sfuConnection && peer.connectionStatus === 'connected')
                .map(([_, peer]) => peer.sessionId)

            const added = _.difference(sessionIds, prevSessionIdsRef.current)
            const removed = _.difference(prevSessionIdsRef.current, sessionIds)

            log.verbose('peerAddedEffect', 'intermediate state', { sessionIds, added, removed })

            if (added.length > 0) {
                let peers: MusePeer[] = Object.entries(roomState.peers)
                    .filter(([_, peer]) => added.includes(peer.sessionId))
                    .map(([_, peer]) => peer)
                onPeerAdded(room, peers)
            }

            if (removed.length > 0) {
                onPeerRemoved(removed)
            }

            if (added.length > 0 || removed.length > 0) {
                videoSubscriptionManager.syncPeersList(roomState.peers)
            }

            prevSessionIdsRef.current = [...sessionIds]
        },
        [roomState.peers]
    )

    // nasty, but have to clear the ref on new room.
    useEffect(
        function resetRefsOnNewRoom() {
            prevSessionIdsRef.current = []

            // @ts-ignore
            window.getSubscribedVideoCount = () => {
                videoSubscriptionManager.getSubscribedVideoCount()
            }
            // @ts-ignore
            window.applyVideoSubsLimit = () => {
                videoSubscriptionManager.applyVideoSubscriptionLimit()
            }
        },
        [roomState.roomUrn]
    )

    const prevConnectionStateRef = useRef<ConnectionState | null>(null)
    const registerRoomEventHandler = useCallback((sfuRoom: Room, sfuRoomInfo: { nearestNode: SfuNode }) => {
        const tags = 'useSfuRoomService,RoomEvent'
        sfuRoom
            .on(RoomEvent.SignalConnected, async () => {
                log.verbose(tags + ',SignalConnected', `room event: SignalConnected!!`)
                log.checkpoint(`sfu_signal_connected`)

                roomDispatch({
                    type: 'update_room_context',
                    value: { sfuRoom, sfuRoomInfo },
                })
            })
            .on(RoomEvent.ConnectionStateChanged, async (state: ConnectionState) => {
                roomDispatch({
                    type: 'update_room_context',
                    value: { sfuRoomConnected: state === 'connected' },
                })

                if (state === 'disconnected' && prevConnectionStateRef.current === 'connecting') {
                    log.error(`sfuRoomDisconnectedWhileConnecting`, ``, {
                        state,
                    })
                    const msg =
                        "We're having trouble connecting you to the room. " +
                        'Please check your internet connection and try again. \n' +
                        'This may also be due to network issues such as a restrictive firewall policy.'
                    leaveRoom({ endSession: false, showDialogWithMessage: msg })
                }

                log.checkpoint(`sfu_room_connection_state_changed`, ``, {
                    state,
                })
                log.verbose(tags + ',ConnectionStateChanged', `room event: ConnectionStateChanged`, {
                    state,
                })
                prevConnectionStateRef.current = state
            })
            .on(RoomEvent.Disconnected, async (reason?: DisconnectReason) => {
                log.verbose(tags + ',Disconnected', `room event: Disconnected`, {
                    reason: reason ? disconnectReasonToJSON(reason) : '(undefined)',
                })
                log.checkpoint(`sfu_room_disconnected`, '', {
                    reason: reason ? disconnectReasonToJSON(reason) : '(undefined)',
                })

                // FIXME: prematurely setting sfuRoom to null before cleanup.
                roomDispatch({
                    type: 'update_room_context',
                    value: { sfuRoom: null },
                })
            })
            .on(RoomEvent.Connected, async () => {
                const sessionIds: string[] = []
                sfuRoom.remoteParticipants.forEach((peer) => {
                    sessionIds.push(peer.identity)
                })
                log.verbose(tags + ',Connected', 'received event "Connected".', {
                    sessionIds: sessionIds,
                })
                log.checkpoint(`sfu_connected_received_peers`, '', {
                    sessionIds: sessionIds,
                })

                if (env.TEST_SFU_ADD_DELAY_AT_CONNECTED) {
                    log.verbose(tags + ',Connected', 'TEST_SFU_ADD_DELAY_AT_CONNECTED set; sleeping.')
                    await sleep(5000)
                }

                if (env.TEST_SFU_TRY_ADD_NONEXISTING_SESSIONID) {
                    sfuAddPeersToContext(['00000000-0000-0000-0000-000000000000'])
                }

                if (roomStateRef.current.isHost && (roomStateRef.current.autoRecordSessions ?? false)) {
                    startCloudRecording(sfuRoomInfo)
                }

                let manifestDeltas: { sessionId: string; partialManifest: PeerManifest }[] = []
                sfuRoom.remoteParticipants.forEach((participant) => {
                    if (!participant.metadata) {
                        return
                    }

                    try {
                        const j = JSON.parse(participant.metadata) as LiveKitMetadata
                        if (j.manifest && j.sessionId) {
                            const { manifest, sessionId } = j
                            LiveKitManifestSupport.markSupported(sessionId)
                            onManifestUpdated(sessionId, manifest)
                            setLastManifest(sessionId, manifest)
                            manifestDeltas.push({ sessionId, partialManifest: manifest })
                        }
                    } catch (e) {
                        log.assert('Connected', 'failed to parse manifest!', { metadata: participant.metadata, e })
                    }
                })

                if (manifestDeltas.length > 0) {
                    roomDispatch({
                        type: 'update_peer_manifest',
                        value: manifestDeltas,
                    })
                }
                await sfuAddPeersToContext(sessionIds)
            })
            .on(RoomEvent.Reconnected, async () => {
                const sessionIds: string[] = []
                sfuRoom.remoteParticipants.forEach((peer) => {
                    sessionIds.push(peer.identity)
                })
                log.verbose(tags + ',Reconnected', 'received event "Connected".', {
                    sessionIds: sessionIds,
                })
                log.checkpoint(`sfu_reconnected_received_peers`, '', {
                    sessionIds: sessionIds,
                })

                if (env.TEST_SFU_ADD_DELAY_AT_CONNECTED) {
                    log.verbose(tags + ',Reconnected', 'TEST_SFU_ADD_DELAY_AT_CONNECTED set; sleeping.')
                    await sleep(5000)
                }

                await sfuAddPeersToContext(sessionIds)
            })
            .on(
                RoomEvent.ParticipantMetadataChanged,
                (_oldMetadata: string | undefined, participant: RemoteParticipant | LocalParticipant) => {
                    if (participant instanceof LocalParticipant) {
                        return
                    }

                    const metadata = participant.metadata

                    const sessionId = participant.identity
                    log.verbose(tags + ',ParticipantMetadataChanged', 'received event "ParticipantMetadataChanged".', {
                        sessionId: sessionId,
                        participantMetadata: metadata,
                    })

                    if (metadata) {
                        try {
                            const j = JSON.parse(metadata) as LiveKitMetadata
                            if (j.manifest) {
                                const { manifest } = j
                                LiveKitManifestSupport.markSupported(sessionId)
                                onManifestUpdated(sessionId, manifest)
                                setLastManifest(sessionId, manifest)
                                roomDispatch({
                                    type: 'update_peer_manifest',
                                    value: {
                                        sessionId: sessionId,
                                        partialManifest: manifest,
                                    },
                                })
                            }
                        } catch (e) {
                            log.assert('ParticipantMetadataChanged', 'failed to parse manifest!', { metadata, e })
                        }
                    }
                }
            )
            .on(RoomEvent.RoomMetadataChanged, (metadata: string) => {
                log.verbose(tags + ',RoomMetadataChanged', 'received event "RoomMetadataChanged".', {
                    'participant.metadata': metadata,
                })
            })
            .on(RoomEvent.ParticipantConnected, async (participant: RemoteParticipant) => {
                const sessionId = participant.identity
                log.verbose(tags + ',ParticipantConnected', 'received event "ParticipantConnected".', {
                    participantIdentity: participant.identity,
                    participantMetadata: participant.metadata,
                })
                log.checkpoint(`sfu_participant_connected`, '', {
                    sessionId: sessionId,
                })

                if (env.TEST_SFU_ADD_DELAY_AT_PARTICIPANT_CONNECTED) {
                    await sleep(5000)
                }

                if (participant.metadata) {
                    try {
                        const j = JSON.parse(participant.metadata) as LiveKitMetadata
                        if (j.manifest && j.sessionId) {
                            const { manifest, sessionId } = j
                            LiveKitManifestSupport.markSupported(sessionId)
                            onManifestUpdated(sessionId, manifest)
                            setLastManifest(sessionId, manifest)
                            roomDispatch({
                                type: 'update_peer_manifest',
                                value: {
                                    sessionId: sessionId,
                                    partialManifest: manifest,
                                },
                            })
                        }
                    } catch (e) {
                        log.assert('ParticipantConnected', 'failed to parse manifest!', {
                            metadata: participant.metadata,
                            e,
                        })
                    }
                }

                await sfuAddPeersToContext([sessionId])
            })
            .on(RoomEvent.AudioPlaybackStatusChanged, () => {
                // https://docs.livekit.io/client-sdk-js/#audio-playback
                if (!sfuRoom.canPlaybackAudio) {
                    log.checkpoint('sfu_audio_playback_status_cannot_playback_audio')

                    roomDispatch({
                        type: 'update_room_context',
                        value: { isCannotPlayAudioDialogOpen: true },
                    })
                }
            })
            .on(RoomEvent.ParticipantDisconnected, (participant: RemoteParticipant) => {
                log.verbose(tags + ',ParticipantDisconnected', 'received event "ParticipantDisconnected".', {
                    'participant.identity': participant.identity,
                })
                log.checkpoint(`sfu_participant_disconnected`, '', {
                    sessionId: participant.identity,
                })

                roomDispatch({
                    type: 'update_peer',
                    value: {
                        sessionId: participant.identity,
                        peer: {
                            connectionStatus: 'closing',
                        },
                    },
                })
            })
            .on(RoomEvent.TrackSubscribed, (_1, pub, participant) => {
                onTrackSubscribed(pub, participant)
            })
            .on(RoomEvent.TrackUnsubscribed, (_, pub, participant) => {
                onTrackUnsubscribed(pub, participant)
            })
            .on(RoomEvent.LocalTrackPublished, (publication: LocalTrackPublication) => {
                console.log(
                    tags + 'LocalTrackPublished',
                    'received event "LocalTrackPublished". publication=%O',
                    publication
                )
            })
            .on(RoomEvent.LocalTrackUnpublished, (publication: LocalTrackPublication) => {
                console.log(
                    tags + 'LocalTrackUnpublished',
                    'received event "LocalTrackUnpublished". publication=%O',
                    publication
                )
            })
            .on(
                RoomEvent.TrackStreamStateChanged,
                (
                    publication: RemoteTrackPublication,
                    streamState: Track.StreamState,
                    participant: RemoteParticipant
                ) => {
                    console.log(`TrackStreamStateChanged: streamState=%O`, streamState)
                }
            )
            .on(RoomEvent.MediaDevicesError, (e: Error) => {
                const failure = MediaDeviceFailure.getFailure(e)
                log.verbose(tags + 'MediaDevicesError', 'received event "MediaDevicesError".', {
                    failure,
                })
            })
            .on(RoomEvent.DataReceived, (payload: Uint8Array, participant?: Participant, kind?: DataPacket_Kind) => {
                const decoder = new TextDecoder()
                try {
                    const body = JSON.parse(decoder.decode(payload))
                    log.isSilly() &&
                        log.silly(tags + 'DataReceived', 'received and decoded payload into body:', {
                            body,
                        })

                    if (participant?.identity) {
                        peerDataChannelEventHandler(body, participant?.identity)
                    } else {
                    }
                } catch (e) {
                    // it is normal for parsing to fail on some data packets such as binary audio data.
                    // such cases are handled in the universalDataReceivedHandler in RoomController.
                    // log.error(tags+'DataReceived', 'failed to decode message:', { e })
                }
            })
    }, [])

    useEffect(
        function attachSfuRoomEventHandlerEffect() {
            ;(async () => {
                if (roomState.expectedNetworkTopology !== 'sfu') {
                    return
                }
                if (roomState.myWaitlistStatus !== 'isAccepted') {
                    return
                }
                if (roomStateRef.current.sfuRoom) {
                    // do not double-spend sfuRoom creation.
                    return
                }

                let livekitNode = await sfuNodeSelection.getNearestNode()
                if (!livekitNode) {
                    log.assert('joinSFURoom', 'sfuNodeSelection.getNearestNode() returned null!', {
                        sfuNodes: sfuNodeSelection.getNodes(),
                    })

                    // default node
                    livekitNode = {
                        name: 'Default',
                        code: 'default',
                        url: Constants.links.sfu_ws_url,
                        latency: 0,
                        lat: 0,
                        lon: 0,
                    }
                }

                log.checkpoint(`call_register_sfu_room`)
                const response = await wsConnection.sendMessage(
                    {
                        type: 'register-sfu-room',
                        data: {
                            authToken: userStateRef.current.authToken,
                            roomUrn: roomStateRef.current.roomUrn,
                            userUrn: userStateRef.current.userUrn,
                            identity: userStateRef.current.sessionId,
                            nearestNode: livekitNode.url,
                        },
                    },
                    {
                        isReliable: true,
                        checkAck: true,
                        tBetweenRetriesMs: 10000,
                        totalTimeoutMs: 10000,
                    }
                )

                const liveKitToken = response?.liveKitToken

                // if the room is a Livekit Cloud room, then the server will
                // return a `liveKitUrl` which we must always use.
                const liveKitUrl: string | null = response?.liveKitUrl || null
                if (liveKitUrl) {
                    livekitNode = {
                        code: 'global',
                        name: 'Global',
                        url: liveKitUrl,
                        lat: 0,
                        lon: 0,
                        latency: 0,
                    }
                }

                log.checkpoint(`register_sfu_room_response`, '', response)

                if (!liveKitToken) {
                    const msg =
                        'Failed to connect to the room. Please check your network connection ' + 'and try again.'
                    leaveRoom({ endSession: false, showDialogWithMessage: msg })
                    return
                }

                // apply override
                if (env.VITE_SFU_SERVER_URL) {
                    livekitNode.url = env.VITE_SFU_SERVER_URL
                    livekitNode.name = env.VITE_SFU_SERVER_URL // for debugging purposes.
                }

                log.checkpoint('select_sfu_node', '', {
                    nodes: sfuNodeSelection.getNodes(),
                    nearestNode: livekitNode,
                    userGeoData: userStateRef.current.profile?.geoData || null,
                })

                const lk = {
                    url: livekitNode.url,
                    token: liveKitToken,
                    connectOptions: {
                        autoSubscribe: env.TEST_NO_SUBSCRIBE_MEDIA ? false : true,
                    },
                }
                log.verbose('joinSFURoom', `creating room.`, { lk })
                log.checkpoint(`sfu_create_room`)
                const sfuRoom = new Room({
                    // automatically manage subscribed video quality
                    adaptiveStream: true,

                    dynacast: true,

                    // default capture settings
                    videoCaptureDefaults: {
                        resolution: VideoPresets.h1080.resolution,
                    },

                    publishDefaults: {
                        dtx: false,
                        simulcast: true,
                        videoSimulcastLayers: [VideoPresets.h540, VideoPresets.h216],
                        stopMicTrackOnMute: true,
                        videoCodec: 'h264',
                        videoEncoding: {
                            maxFramerate: 30,
                            maxBitrate: 5_000_000,
                        },
                    },
                })
                applyLivekitStereoPatch(sfuRoom)
                videoSubscriptionManager.setSfuRoom(sfuRoom)

                log.verbose('joinSFURoom', `created room object. calling connect()`, {
                    params: lk,
                })
                sfuRoom.connect(lk.url, lk.token, lk.connectOptions)

                const sfuRoomInfo = { nearestNode: livekitNode }
                registerRoomEventHandler(sfuRoom, sfuRoomInfo)
            })()
        },
        [
            roomState.expectedNetworkTopology,
            roomState.myWaitlistStatus,
            registerRoomEventHandler,
            roomStateRef,
            userStateRef,
        ]
    )

    useEffect(
        function mergeRemoteDevicesByTopology() {
            const deltaMap: Record<string, { sessionId: string; peer: Partial<MusePeer> }> = {}

            if (roomState.expectedNetworkTopology === 'sfu') {
                Object.keys(roomState.peers).forEach((sessionId) => {
                    const peer = roomState.peers[sessionId]

                    if (deltaMap[sessionId] == null) {
                        deltaMap[sessionId] = { sessionId, peer: {} }
                    }

                    if (peer.remoteSfuVideo && peer.remoteSfuVideo !== peer.remoteVideo) {
                        log.verbose(`mergeRemoteDevicesByTopology`, `setting remoteVideo := remoteSfuVideo`)
                        deltaMap[sessionId].peer.remoteVideo = peer.remoteSfuVideo
                    }

                    if (peer.remoteSfuDesktop && peer.remoteSfuDesktop !== peer.remoteDesktop) {
                        log.verbose(`setRemoteDevices`, `setting remoteDesktop := remoteSfuDesktop`)
                        deltaMap[sessionId].peer.remoteDesktop = peer.remoteSfuDesktop
                    }

                    // Suppose:
                    // peer.remoteSfuAudios = [ z1, a1, b1, c1     ]
                    // peer.remoteP2pAudios = [         b2, c2, d2 ]
                    // peer.remoteAudios    = [     a1, b1, c2, d2 ]
                    // and the goal is to merge sfu -> p2p, into `remoteAudios`.
                    //
                    // Loop through remoteSfuAudios, comparing with remoteAudios. z1 -> (null) insert; a1 -> a1 no-op; b1 -> b1 no-op; c1 -> c2 replace.
                    // Then, loop through `remoteAudios` and see if each element exists in either `remoteSfuAudios` or `remoteP2pAudios`.
                    // If exists in neither, then remove the element.

                    let remoteAudiosNext = [...peer.remoteAudios]
                    peer.remoteSfuAudios.forEach((a) => {
                        let i = remoteAudiosNext.findIndex((b) => b.info.deviceId === a.deviceId)
                        if (i >= 0) {
                            const p2pAudio = remoteAudiosNext[i]
                            if (p2pAudio.fromTopology === 'p2p') {
                                // try {
                                //     p2pAudio.track.stop()
                                // } catch(e) { }
                                // replace
                                remoteAudiosNext[i] = a
                            }
                        } else {
                            // insert
                            remoteAudiosNext.push(a)
                        }
                    })

                    const d = arrayDiff(remoteAudiosNext, peer.remoteAudios, (a, b) => a.info === b.info)
                    if (d.added.length > 0 || d.removed.length > 0) {
                        log.verbose(`mergeRemoteDevicesByTopology`, `setting remoteAudios := remoteSfuAudios`, {
                            remoteAudiosNext: remoteAudiosNext.map((a) => ({
                                label: a.info.label,
                                fromTopology: a.fromTopology,
                            })),
                        })
                        deltaMap[sessionId].peer.remoteAudios = remoteAudiosNext
                    }
                })
            } else if (roomState.expectedNetworkTopology === 'p2p') {
                Object.keys(roomState.peers).forEach((sessionId) => {
                    const peer = roomState.peers[sessionId]

                    if (deltaMap[sessionId] == null) {
                        deltaMap[sessionId] = { sessionId, peer: {} }
                    }

                    if (peer.remoteP2pVideo && peer.remoteP2pVideo !== peer.remoteVideo) {
                        log.verbose(`setRemoteDevices`, `setting remoteVideo := remoteP2pVideo`)
                        deltaMap[sessionId].peer.remoteVideo = peer.remoteP2pVideo
                    }

                    if (peer.remoteP2pDesktop && peer.remoteP2pDesktop !== peer.remoteDesktop) {
                        log.verbose(`setRemoteDevices`, `setting remoteDesktop := remoteP2pDesktop`)
                        deltaMap[sessionId].peer.remoteDesktop = peer.remoteP2pDesktop
                    }

                    let remoteAudiosNext = [...peer.remoteAudios]
                    peer.remoteP2pAudios.forEach((a) => {
                        let i = remoteAudiosNext.findIndex((b) => a.info && b.info.deviceId === a.info.deviceId)
                        if (i >= 0) {
                            const sfuAudio = remoteAudiosNext[i]
                            if (sfuAudio.fromTopology === 'p2p') {
                                // try {
                                //     sfuAudio.track.stop()
                                // } catch(e) { }
                                // replace
                                remoteAudiosNext[i] = a
                            }
                        } else {
                            // insert
                            remoteAudiosNext.push(a)
                        }
                    })

                    const d = arrayDiff(remoteAudiosNext, peer.remoteAudios, (a, b) => a.info === b.info)
                    if (d.added.length > 0 || d.removed.length > 0) {
                        log.verbose(`mergeRemoteDevicesByTopology`, `setting remoteAudios := remoteP2pAudios`)
                        deltaMap[sessionId].peer.remoteAudios = remoteAudiosNext
                    }
                })
            }

            const deltas = Object.values(deltaMap).filter((d) => Object.keys(d.peer).length > 0)
            if (deltas.length > 0) {
                roomDispatch({
                    type: 'update_peer',
                    value: deltas,
                })
            }
        },
        [roomDispatch, roomState.expectedNetworkTopology, roomState.peers]
    )

    const sfuVideoTrackPublishHelperRemoveTrackTimerRef = useRef<NodeJS.Timeout | undefined>(undefined)
    const sfuVideoTrackPublishHelper = useCallback(
        (
            sfuRoom: Room,
            videoOrDesktopStream: LocalVideoTrack | MediaStream | null,
            deviceId: string,
            source: Track.Source
        ) => {
            log.verbose('videoTrackStasis', `start.`)

            const currPublishedTracks: MediaStreamTrack[] = (() => {
                // { trackName, trackSid, trackInfo, track, ... }[]
                const currRoomTracks: IterableIterator<LocalTrackPublication> =
                    sfuRoom.localParticipant.videoTrackPublications.values()
                const tracks = []
                for (let { track } of currRoomTracks) {
                    if (track?.mediaStreamTrack) {
                        if (track.source === source) {
                            tracks.push(track.mediaStreamTrack)
                        }
                    }
                }
                return tracks
            })()
            const currPublishedVideoTrackIds = currPublishedTracks.map((t) => t.id)

            let currVideoSelectionTrack: LocalVideoTrack | MediaStreamTrack | null = null

            if (videoOrDesktopStream instanceof LocalVideoTrack) {
                currVideoSelectionTrack = videoOrDesktopStream
            } else if (videoOrDesktopStream instanceof MediaStream) {
                currVideoSelectionTrack = extractVideoTrack(videoOrDesktopStream)
            }

            const currVideoSelectionId =
                currVideoSelectionTrack instanceof LocalVideoTrack
                    ? currVideoSelectionTrack.mediaStreamTrack.id
                    : currVideoSelectionTrack?.id || null

            log.verbose(`videoTrackStasis`, `currently published vs selected track ids:`, {
                currPublishedVideoTrackIds,
                currVideoSelectionId,
            })

            const { added: addedTracks, removed: removedTracks } = arrayDiff(
                currVideoSelectionTrack ? [currVideoSelectionTrack] : [],
                currPublishedTracks,
                (a, b) => a.id === b.id
            )
            log.verbose(`videoTrackStasis`, `diff`, {
                addedTrack: addedTracks.map((t) => t.id),
                removedTrack: removedTracks.map((t) => t.id),
            })
            if (addedTracks.length > 0) {
                const addedTrack = addedTracks[0]
                const label =
                    addedTrack instanceof LocalVideoTrack ? addedTrack.mediaStreamTrack.label : addedTrack.label

                if (addedTrack instanceof LocalVideoTrack && source === Track.Source.ScreenShare) {
                    addedTrack.setDegradationPreference('maintain-resolution')
                    addedTrack.setPublishingQuality(VideoQuality.HIGH)
                }
                log.checkpoint(`sfu_publish_video_track`, ``, { label, deviceId: deviceId })
                sfuRoom.localParticipant.publishTrack(addedTrack, {
                    name: JSON.stringify({ label, deviceId }),
                    source: source,
                    simulcast: source === Track.Source.ScreenShare ? false : true,
                })
            }
            if (removedTracks.length > 0) {
                // unpublish if needed
                log.verbose(`videoTrackStasis`, `unpublishTrack (set timeout)`, {
                    trackIds: removedTracks.map((t) => t.id),
                })

                // timeout is there for implementing a poor-man's "replace track".
                const timeoutMs = addedTracks.length > 0 ? 5000 : 0

                clearTimeout(sfuVideoTrackPublishHelperRemoveTrackTimerRef.current)
                sfuVideoTrackPublishHelperRemoveTrackTimerRef.current = setTimeout(() => {
                    try {
                        log.checkpoint(`sfu_unpublish_video_tracks`, ``, { trackIds: removedTracks.map((t) => t.id) })
                        sfuRoom.localParticipant.unpublishTracks(removedTracks)
                    } catch (e) {}
                }, timeoutMs)
            }
        },
        []
    )

    useEffect(
        function sfuVideoTrackPublishManager() {
            if (roomState.sfuRoom == null) {
                log.verbose('videoTrackStasis', `no-op because roomState.sfuRoom is null.`)
                return
            }

            log.verbose('videoTrackStasis', `calling videoTrackStasisHelper.`)
            sfuVideoTrackPublishHelper(
                roomState.sfuRoom,
                state.mediaStreams.video instanceof LocalVideoTrack && state.mediaStreams.video
                    ? state.mediaStreams.video
                    : null,
                state.mediaSelection.video,
                Track.Source.Camera
            )
        },
        [roomState.sfuRoom, state.mediaSelection.video, state.mediaStreams.video, sfuVideoTrackPublishHelper]
    )

    useEffect(
        function sfuDesktopTrackPublishManager() {
            if (roomState.sfuRoom == null) {
                log.verbose('desktopTrackStasis', `no-op because roomState.sfuRoom is null.`)
                return
            }

            log.verbose('desktopTrackStasis', `calling videoTrackStasisHelper.`)
            sfuVideoTrackPublishHelper(
                roomState.sfuRoom,
                state.mediaStreams.desktop instanceof MediaStream ? state.mediaStreams.desktop : null,
                state.mediaSelection.desktop,
                Track.Source.ScreenShare
            )
        },
        [roomState.sfuRoom, state.mediaSelection.desktop, state.mediaStreams.desktop, sfuVideoTrackPublishHelper]
    )

    const _testInitialAudioPublishFailTracker = useRef<{ [key: string]: number }>({})
    const sfuAudioTrackPublisher = useCallback(async (sfuRoom: Room | null) => {
        log.verbose('sfuAudioTrackPublisher', `start.`)

        if (sfuRoom == null) {
            log.verbose('sfuAudioTrackPublisher', `no-op because sfuRoom is null.`)
            return { retry: false }
        }

        const currPublishedTracks: MediaStreamTrack[] = (() => {
            {
                // note: don't reuse the `currRoomTracks` iterator.
                const currRoomTracks: IterableIterator<LocalTrackPublication> =
                    sfuRoom.localParticipant.audioTrackPublications.values()
                log.verbose(`sfuAudioTrackPublisher`, `currRoomTracks=`, {
                    ids: [...currRoomTracks].map((t) => t.track?.mediaStreamTrack.id),
                })
            }

            const tracks: MediaStreamTrack[] = []
            sfuRoom.localParticipant.audioTrackPublications.forEach((t: LocalTrackPublication) => {
                if (t.kind === Track.Kind.Audio && t.track?.mediaStreamTrack) {
                    tracks.push(t.track.mediaStreamTrack)
                }
            })

            // also include audioEngine streams
            const audioEngineStreams = roomStateRef.current.publishedSfuAudios.filter(
                (a) => a.info.audioEngineStreamUuid
            )
            audioEngineStreams.forEach((a) => {
                if (tracks.find((b) => b.id === a.id) == null) {
                    tracks.push(a)
                }
            })

            return tracks
        })()

        const { added: addedTracks, removed: removedTracks } = arrayDiff(
            userStateRef.current.mediaStreams.audioInputs,
            currPublishedTracks,
            (a, b) => a.id === b.id
        )
        log.verbose(`sfuAudioTrackPublisher`, `diff`, {
            addedTrack: addedTracks.map((t) => t.id),
            removedTrack: removedTracks.map((t) => t.id),
        })

        let publishedSfuAudiosNext = [...roomStateRef.current.publishedSfuAudios]
        let publishedSfuAudiosModified = false

        addedTracks.length > 0 &&
            log.verbose(`sfuAudioTrackPublisher`, `publishTrack`, {
                trackId: addedTracks.map((t) => t.id),
            })

        let failedTracksCt = 0
        for (let addedTrack of addedTracks) {
            // `addedMediaStreamTrack` is what gets sent to publishTrack().
            let addedMediaStreamTrack = addedTrack as MediaStreamTrack

            if (addedTrack.info.audioEngineStreamUuid) {
                log.verbose(
                    `sfuAudioTrackPublisher`,
                    `calling audioEngineRoom.sendLocalStream because ` + `audioEngineStreamUuid is set.`,
                    {
                        deviceId: addedTrack.info.deviceId,
                        displayLabel: addedTrack.info.displayLabel,
                    }
                )

                if (roomStateRef.current.audioEngineRoom) {
                    roomStateRef.current.audioEngineRoom.sendLocalStream(addedTrack)
                }
            } else {
                try {
                    if (env.TEST_INITIAL_AUDIO_PUBLISH_FAIL_RECOVERY) {
                        if (_testInitialAudioPublishFailTracker.current[addedTrack.id] == null) {
                            _testInitialAudioPublishFailTracker.current[addedTrack.id] = 1
                            throw new Error('TEST_INITIAL_AUDIO_PUBLISH_FAIL_RECOVERY')
                        }
                    }

                    const trackName = JSON.stringify({
                        label: addedTrack.info.displayLabel,
                        deviceId: addedTrack.info.deviceId,
                    })

                    log.checkpoint(`sfu_publish_audio_track`, ``, {
                        label: addedTrack.info.displayLabel,
                        deviceId: addedTrack.info.deviceId,
                    })
                    await sfuRoom.localParticipant.publishTrack(addedMediaStreamTrack, {
                        source: Track.Source.Microphone,
                        name: trackName,
                        audioPreset: {
                            maxBitrate: Constants.default_sfu_audio_bitrate,
                            priority: 'high',
                        },
                        simulcast: false,
                    })
                } catch (e) {
                    log.error(`sfuAudioTrackPublisher`, `publishTrack() threw an error.`, { e })
                    failedTracksCt += 1
                }
            }

            if (publishedSfuAudiosNext.find((a) => a.id === addedTrack.id) == null) {
                publishedSfuAudiosNext.push(addedTrack)
                publishedSfuAudiosModified = true
            }
        }

        if (removedTracks.length > 0) {
            try {
                log.verbose(`sfuAudioTrackPublisher`, `unpublishTrack`, {
                    trackIds: removedTracks.map((t) => t.id),
                    'roomStateRef/current/publishedSfuAudios': roomStateRef.current.publishedSfuAudios.map((a) => a.id),
                })

                // const publishedSfuAudiosNext = [ ...roomStateRef.current.publishedSfuAudios ]
                arrayInPlaceRemove(publishedSfuAudiosNext, (a) => {
                    const doRemove = !!removedTracks.find((b) => a.id === b.id)
                    log.verbose(
                        `sfuAudioTrackPublisher`,
                        `found matching id in removedTracks; removing in-place from publishedSfuAudiosNext.`,
                        { id: a.id }
                    )
                    if (doRemove) {
                        publishedSfuAudiosModified = true
                    }
                    return doRemove
                })

                log.verbose(`sfuAudioTrackPublisher`, `calling roomDispatch update_room_context:`, {
                    publishedSfuAudiosNext: publishedSfuAudiosNext.map((a) => a.id),
                })

                log.checkpoint(`sfu_unpublish_audio_tracks`, ``, { trackIds: removedTracks.map((t) => t.id) })
                await sfuRoom.localParticipant.unpublishTracks(removedTracks)
            } catch (e) {
                log.error(`sfuAudioTrackPublisher`, `unpublishTrack() threw an error.`, { e })
            }
        }

        checkRemotePeersAudioEngineCompatibilityAndShowDialog(addedTracks, roomStateRef.current.peers)

        if (publishedSfuAudiosModified) {
            roomDispatch({
                type: 'update_room_context',
                value: {
                    publishedSfuAudios: publishedSfuAudiosNext,
                },
            })
        }

        return { retry: failedTracksCt > 0 }
    }, [])

    const sfuAudioTrackPublishWithRetry = useCallback(async (sfuRoom: Room | null) => {
        clearInterval(sfuAudioTrackPublisherRetryTimerRef.current)

        const { retry } = await sfuAudioTrackPublisher(sfuRoom)
        if (retry) {
            log.info(`sfuAudioTrackPublisherEffect`, `retrying sfuAudioTrackPublisher() in 3s.`)
            sfuAudioTrackPublisherRetryTimerRef.current = setInterval(async () => {
                const { retry } = await sfuAudioTrackPublisher(sfuRoom)
                if (!retry) {
                    clearInterval(sfuAudioTrackPublisherRetryTimerRef.current)
                    log.info(`sfuAudioTrackPublisherEffect`, `sfuAudioTrackPublisher() retry completed successfully.`)
                } else {
                    log.info(`sfuAudioTrackPublisherEffect`, `retrying sfuAudioTrackPublisher() in 3s.`)
                }
            }, 3000)
        }
    }, [])

    let sfuAudioTrackPublisherRetryTimerRef = useRef<NodeJS.Timeout | undefined>(undefined)
    useEffect(() => {
        // NOTE: uses `state.mediaStreams.audioInputs`
        sfuAudioTrackPublishWithRetry(roomState.sfuRoom)
    }, [roomState.sfuRoom, state.mediaStreams.audioInputs])

    // React to my midi devices changing and add/remove/update them in `roomState.publishedSfuMidi`.
    useEffect(
        function sfuMidiDevicePublishManager() {
            if (roomState.sfuRoom == null) {
                log.verbose('midiDeviceStasis', `no-op because roomState.sfuRoom is null.`)
                return
            }

            const midiIds: string[] = isDefinedDeviceId(state.mediaSelection.midiInput)
                ? [state.mediaSelection.midiInput]
                : []

            const { added, removed } = arrayDiff(
                midiIds,
                roomStateRef.current.publishedSfuMidi,
                (id, b) => id === b.sourceId
            )

            log.verbose(`midiDeviceStasis`, `difference between my midi devices and roomState.publishedSfuMidi:`, {
                added: added.map((a) => ({ sourceId: a })),
                removed: removed.map((b) => ({ sourceId: b.sourceId, label: b.label })),
            })

            if (added.length + removed.length === 0) {
                log.verbose(`midiDeviceStasis`, `no difference so no-op.`)
                return
            }

            const outMidiNext = [...roomStateRef.current.publishedSfuMidi]

            for (let addedSourceId of added) {
                // resolve
                const d = state.mediaSources.midiInputs.find((a) => a.id === addedSourceId)
                if (d) {
                    outMidiNext.push({
                        sourceId: d.id,
                        label: d.name || d.id,
                        isMuted: false,
                    })
                }
            }

            for (let removedDevice of removed) {
                arrayInPlaceRemove(outMidiNext, (v) => v.sourceId === removedDevice.sourceId)
            }

            roomDispatch({
                type: 'update_room_context',
                value: {
                    publishedSfuMidi: outMidiNext,
                },
            })
        },
        [roomDispatch, roomState.sfuRoom, roomStateRef, state.mediaSelection.midiInput, state.mediaSources.midiInputs]
    )

    useEffect(() => {
        const roomUrn = roomState.roomUrn
        const sessionId = state.sessionId

        if (!roomUrn || !sessionId) {
            return
        }

        const speakerActivityChangedHandler = (isSpeaking: boolean) => {
            log.verbose('speakerActivityChangedHandler', `triggered. isSpeaking=${isSpeaking}.`)
            if (isSpeaking) {
                log.verbose('speakerActivityChangedHandler', `calling sfuAudioTrackPublishWithRetry().`)
                if (!env.TEST_NO_SUBSCRIBE_MEDIA) {
                    sfuAudioTrackPublishWithRetry(roomStateRef.current.sfuRoom)
                }
            }

            log.verbose('speakerActivityChangedHandler', `sending 'speaker-detection-update'.`)
            wsConnection.sendMessage({
                type: 'speaker-detection-update',
                data: {
                    roomUrn,
                    sessionId,
                    isSpeaking,
                },
            })
        }

        log.verbose('speakerActivityChangedHandler', `registering event listener.`)
        speakerDetectionManager.on('speakerActivityChanged', speakerActivityChangedHandler)

        return () => {
            speakerDetectionManager.off('speakerActivityChanged', speakerActivityChangedHandler)
        }
    }, [wsConnection, roomState.roomUrn, state.sessionId])

    return {}
}

export function disconnectReasonToJSON(object: DisconnectReason): string {
    switch (object) {
        case DisconnectReason.UNKNOWN_REASON:
            return 'UNKNOWN_REASON'
        case DisconnectReason.CLIENT_INITIATED:
            return 'CLIENT_INITIATED'
        case DisconnectReason.DUPLICATE_IDENTITY:
            return 'DUPLICATE_IDENTITY'
        case DisconnectReason.SERVER_SHUTDOWN:
            return 'SERVER_SHUTDOWN'
        case DisconnectReason.PARTICIPANT_REMOVED:
            return 'PARTICIPANT_REMOVED'
        case DisconnectReason.ROOM_DELETED:
            return 'ROOM_DELETED'
        case DisconnectReason.STATE_MISMATCH:
            return 'STATE_MISMATCH'
        case DisconnectReason.JOIN_FAILURE:
            return 'JOIN_FAILURE'
        default:
            return 'UNRECOGNIZED'
    }
}
